/**
 * Feature-scoped MobX store coordinating the workspace foundation
 * (ADR-UI-001 decision 3/5/6). It is the hand-rolled `LayoutManager`
 * implementation: all panel operations are expressed in adapter-neutral
 * descriptors, so a future Dockview engine can replace this class behind the
 * same port. It additionally owns device mode, the workspace-local i18n lookup,
 * canvas bounds and persistence orchestration.
 */
import { makeAutoObservable, runInAction } from "mobx";
import { movePanelBy, movePanelTo, resizePanelBy, resizePanelTo } from "./geometry";
import { CURRENT_SCHEMA_VERSION, migrateLayout, serializeLayout, toPlainLayout } from "./migration";
import { detectDeviceModeFromWindow, resolveRoleDefaultPreset } from "./presets";
import { localStorageLayoutStore, type LayoutStore } from "./layout-store";
import { localStorageWorkspacePreferences, type WorkspacePreferenceStore } from "./workspace-preferences";
import { workspaceText, type WorkspaceLocale, type WorkspaceMessageKey } from "./i18n";
import type { LayoutManager } from "./layout-manager";
import type {
  CanvasBounds,
  DeviceMode,
  PanelDescriptor,
  PanelPosition,
  PanelSize,
  WorkspaceLayout,
  WorkspacePreset,
  WorkspaceSlot,
} from "./types";

export type WorkspaceNotice = "layout-load-failed" | "layout-save-failed" | null;

export interface WorkspaceStoreOptions {
  readonly role?: string;
  readonly layoutStore?: LayoutStore;
  readonly preferences?: WorkspacePreferenceStore;
  readonly locale?: WorkspaceLocale;
  readonly initialDeviceMode?: DeviceMode;
  readonly resolvePreset?: (input: { role: string; deviceMode: DeviceMode }) => WorkspacePreset;
  /** When given, panels with an unknown id are dropped on load. */
  readonly knownPanelIds?: ReadonlySet<string>;
}

export class WorkspaceStore implements LayoutManager {
  layout: WorkspaceLayout;
  deviceMode: DeviceMode;
  locale: WorkspaceLocale;
  canvasBounds: CanvasBounds | null = null;
  notice: WorkspaceNotice = null;
  loading = false;

  private readonly role: string;
  private readonly layoutStore: LayoutStore;
  private readonly preferences: WorkspacePreferenceStore;
  private readonly resolvePreset: (input: { role: string; deviceMode: DeviceMode }) => WorkspacePreset;
  private readonly knownPanelIds: ReadonlySet<string> | undefined;
  /** Last-known persisted desktop layout, so device-mode toggles are instant. */
  private userDesktopLayout: WorkspaceLayout | null = null;

  constructor(options: WorkspaceStoreOptions = {}) {
    this.role = options.role ?? "operations";
    this.layoutStore = options.layoutStore ?? localStorageLayoutStore;
    this.preferences = options.preferences ?? localStorageWorkspacePreferences;
    this.resolvePreset = options.resolvePreset ?? resolveRoleDefaultPreset;
    this.knownPanelIds = options.knownPanelIds;
    this.locale = options.locale ?? "en";

    this.deviceMode =
      options.initialDeviceMode ?? this.preferences.readDeviceMode() ?? detectDeviceModeFromWindow();
    // Never blank: start on the role-default preset for the active mode.
    this.layout = this.presetLayout(this.deviceMode);

    makeAutoObservable(this, {}, { autoBind: true });
  }

  // --- device mode & i18n ---------------------------------------------------

  get storageKey(): string {
    return `${this.role}`;
  }

  text(key: WorkspaceMessageKey): string {
    return workspaceText(this.locale, key);
  }

  setLocale(locale: WorkspaceLocale): void {
    this.locale = locale;
  }

  setDeviceMode(mode: DeviceMode): void {
    this.preferences.writeDeviceMode(mode);
    this.deviceMode = mode;
    this.layout =
      mode === "desktop"
        ? this.userDesktopLayout ?? this.presetLayout("desktop")
        : this.presetLayout(mode);
  }

  setCanvasBounds(bounds: CanvasBounds): void {
    this.canvasBounds = bounds;
  }

  // --- LayoutManager: reads -------------------------------------------------

  get panels(): readonly PanelDescriptor[] {
    return [...this.layout.panels].sort((a, b) => a.order - b.order);
  }

  getPanel(id: string): PanelDescriptor | null {
    return this.layout.panels.find((panel) => panel.id === id) ?? null;
  }

  // --- LayoutManager: mutations --------------------------------------------

  addPanel(descriptor: PanelDescriptor): void {
    if (this.layout.panels.some((panel) => panel.id === descriptor.id)) return;
    this.layout = { ...this.layout, panels: [...this.layout.panels, descriptor] };
  }

  removePanel(id: string): void {
    this.layout = { ...this.layout, panels: this.layout.panels.filter((panel) => panel.id !== id) };
  }

  setPanelPosition(id: string, position: PanelPosition): void {
    this.updatePanel(id, (panel) => movePanelTo(panel, position, this.canvasBounds));
  }

  setPanelSize(id: string, size: PanelSize): void {
    this.updatePanel(id, (panel) => resizePanelTo(panel, size, this.canvasBounds));
  }

  movePanelBy(id: string, dx: number, dy: number): void {
    this.updatePanel(id, (panel) => movePanelBy(panel, dx, dy, this.canvasBounds));
  }

  resizePanelBy(id: string, dw: number, dh: number): void {
    this.updatePanel(id, (panel) => resizePanelBy(panel, dw, dh, this.canvasBounds));
  }

  setPanelSlot(id: string, slot: WorkspaceSlot): void {
    this.updatePanel(id, (panel) => ({ ...panel, slot }));
  }

  setCollapsed(id: string, collapsed: boolean): void {
    this.updatePanel(id, (panel) => ({ ...panel, collapsed }));
  }

  toggleCollapsed(id: string): void {
    const panel = this.getPanel(id);
    if (panel !== null) this.setCollapsed(id, !panel.collapsed);
  }

  setFullscreen(id: string, fullscreen: boolean): void {
    // At most one panel is fullscreen at a time.
    this.layout = {
      ...this.layout,
      panels: this.layout.panels.map((panel) => {
        if (panel.id === id) return { ...panel, fullscreen };
        return fullscreen && panel.fullscreen ? { ...panel, fullscreen: false } : panel;
      }),
    };
  }

  toggleFullscreen(id: string): void {
    const panel = this.getPanel(id);
    if (panel !== null) this.setFullscreen(id, !panel.fullscreen);
  }

  loadLayout(layout: WorkspaceLayout): void {
    this.layout = toPlainLayout(layout);
  }

  async saveLayout(): Promise<void> {
    if (this.deviceMode !== "desktop") return; // tablet/mobile are fixed shells
    const snapshot = this.snapshot();
    this.userDesktopLayout = snapshot;
    try {
      await this.layoutStore.save(this.storageKey, snapshot);
      runInAction(() => {
        this.notice = null;
      });
    } catch {
      runInAction(() => {
        this.notice = "layout-save-failed";
      });
    }
  }

  resetLayout(): void {
    this.userDesktopLayout = null;
    this.layout = this.presetLayout(this.deviceMode);
    this.notice = null;
    // Fire-and-forget: the in-memory reset already happened.
    void this.layoutStore.clear(this.storageKey).catch(() => undefined);
  }

  // --- persistence read -----------------------------------------------------

  /** Reads any persisted desktop layout, migrating it or falling back safely. */
  async hydrate(): Promise<void> {
    if (this.deviceMode !== "desktop") return;
    this.loading = true;
    const fallback = this.presetLayout("desktop");
    try {
      const raw = await this.layoutStore.load(this.storageKey);
      const migrated = migrateLayout(
        raw,
        fallback,
        this.knownPanelIds === undefined ? undefined : { knownPanelIds: this.knownPanelIds },
      );
      runInAction(() => {
        this.layout = migrated;
        this.userDesktopLayout = raw === null ? null : migrated;
        this.loading = false;
      });
    } catch {
      runInAction(() => {
        this.layout = fallback;
        this.notice = "layout-load-failed";
        this.loading = false;
      });
    }
  }

  // --- snapshots ------------------------------------------------------------

  snapshot(): WorkspaceLayout {
    return toPlainLayout({ ...this.layout, schemaVersion: CURRENT_SCHEMA_VERSION });
  }

  serialize(): string {
    return serializeLayout(this.layout);
  }

  // --- internals ------------------------------------------------------------

  private presetLayout(mode: DeviceMode): WorkspaceLayout {
    return toPlainLayout(this.resolvePreset({ role: this.role, deviceMode: mode }).layout);
  }

  private updatePanel(id: string, updater: (panel: PanelDescriptor) => PanelDescriptor): void {
    this.layout = {
      ...this.layout,
      panels: this.layout.panels.map((panel) => (panel.id === id ? updater(panel) : panel)),
    };
  }
}
